import React, { useState, useEffect } from 'react';
import { feedsAPI, keywordsAPI, articlesAPI } from '../api';
import './Dashboard.css';

function Dashboard({ user, onLogout }) {
  const [feeds, setFeeds] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [sentArticles, setSentArticles] = useState([]);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('feeds');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadData();
  }, [user.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [feedsRes, keywordsRes, articlesRes] = await Promise.all([
        feedsAPI.getFeeds(user.id),
        keywordsAPI.getKeywords(user.id),
        articlesAPI.getSentArticles(user.id, 20)
      ]);

      setFeeds(feedsRes.data);
      setKeywords(keywordsRes.data);
      setSentArticles(articlesRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeed = async (e) => {
    e.preventDefault();
    
    if (!newFeedUrl.trim()) {
      return;
    }

    try {
      const response = await feedsAPI.addFeed(user.id, newFeedUrl.trim());
      setFeeds([...feeds, response.data]);
      setNewFeedUrl('');
      showSuccess('RSS feed added successfully!');
    } catch (err) {
      setError('Failed to add feed. Please check the URL.');
    }
  };

  const handleDeleteFeed = async (feedId) => {
    try {
      await feedsAPI.deleteFeed(feedId);
      setFeeds(feeds.filter(f => f.id !== feedId));
      showSuccess('RSS feed deleted!');
    } catch (err) {
      setError('Failed to delete feed');
    }
  };

  const handleAddKeyword = async (e) => {
    e.preventDefault();
    
    if (!newKeyword.trim()) {
      return;
    }

    try {
      const response = await keywordsAPI.addKeyword(user.id, newKeyword.trim());
      setKeywords([...keywords, response.data]);
      setNewKeyword('');
      showSuccess('Keyword added successfully!');
    } catch (err) {
      setError('Failed to add keyword');
    }
  };

  const handleDeleteKeyword = async (keywordId) => {
    try {
      await keywordsAPI.deleteKeyword(keywordId);
      setKeywords(keywords.filter(k => k.id !== keywordId));
      showSuccess('Keyword deleted!');
    } catch (err) {
      setError('Failed to delete keyword');
    }
  };

  const handleManualCheck = async () => {
    try {
      setSuccessMessage('Checking feeds...');
      await articlesAPI.triggerFeedCheck();
      showSuccess('Feed check completed! Check your email for any matches.');
      setTimeout(() => loadData(), 2000);
    } catch (err) {
      setError('Failed to check feeds');
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>RSS Monitor</h1>
          <p>Welcome, {user.firstname}! ({user.email})</p>
        </div>
        <div className="header-actions">
          <button onClick={handleManualCheck} className="btn-secondary">
            Check Feeds Now
          </button>
          <button onClick={onLogout} className="btn-logout">
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          {successMessage}
        </div>
      )}

      <div className="dashboard-content">
        <div className="tabs">
          <button
            className={activeTab === 'feeds' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('feeds')}
          >
            RSS Feeds ({feeds.length})
          </button>
          <button
            className={activeTab === 'keywords' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('keywords')}
          >
            Keywords ({keywords.length})
          </button>
          <button
            className={activeTab === 'history' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('history')}
          >
            Sent Articles ({sentArticles.length})
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'feeds' && (
            <div className="section">
              <h2>Your RSS Feeds</h2>
              <form onSubmit={handleAddFeed} className="add-form">
                <input
                  type="url"
                  value={newFeedUrl}
                  onChange={(e) => setNewFeedUrl(e.target.value)}
                  placeholder="Enter RSS feed URL (e.g., https://example.com/feed.xml)"
                  className="input-field"
                />
                <button type="submit" className="btn-add">
                  Add Feed
                </button>
              </form>

              {feeds.length === 0 ? (
                <p className="empty-state">No RSS feeds added yet. Add one above!</p>
              ) : (
                <div className="list">
                  {feeds.map((feed) => (
                    <div key={feed.id} className="list-item">
                      <div className="item-content">
                        <div className="item-url">{feed.url}</div>
                        <div className="item-date">
                          Added {new Date(feed.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteFeed(feed.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'keywords' && (
            <div className="section">
              <h2>Your Keywords</h2>
              <form onSubmit={handleAddKeyword} className="add-form">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Enter keyword to monitor (e.g., artificial intelligence)"
                  className="input-field"
                />
                <button type="submit" className="btn-add">
                  Add Keyword
                </button>
              </form>

              {keywords.length === 0 ? (
                <p className="empty-state">No keywords added yet. Add one above!</p>
              ) : (
                <div className="list">
                  {keywords.map((keyword) => (
                    <div key={keyword.id} className="list-item">
                      <div className="item-content">
                        <div className="item-keyword">{keyword.keyword}</div>
                        <div className="item-date">
                          Added {new Date(keyword.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteKeyword(keyword.id)}
                        className="btn-delete"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="section">
              <h2>Articles Sent to You</h2>
              {sentArticles.length === 0 ? (
                <p className="empty-state">
                  No articles sent yet. Add feeds and keywords, and we'll notify you when matches are found!
                </p>
              ) : (
                <div className="articles-list">
                  {sentArticles.map((article) => (
                    <div key={article.id} className="article-item">
                      <div className="article-header">
                        <span className="article-keyword">"{article.keyword_matched}"</span>
                        <span className="article-date">
                          {new Date(article.sent_at).toLocaleString()}
                        </span>
                      </div>
                      <a
                        href={article.article_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="article-link"
                      >
                        {article.article_link}
                      </a>
                      <div className="article-feed">
                        From: {article.feed_url}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <p>Feeds are checked automatically every 30 minutes</p>
      </footer>
    </div>
  );
}

export default Dashboard;
